import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft,
    Edit3,
    Trash2,
    Check,
    X,
    Plus,
    Calendar,
    Clock,
    Flag,
    FolderOpen,
    CheckCircle,
    Circle,
    Save,
    Repeat,
    Bell,
} from 'lucide-react';
import { useAppState, Task } from '../../contexts/AppContext';

export default function AppTaskDetail() {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const {
        tasks,
        projects,
        updateTask,
        deleteTask,
        toggleTaskComplete,
        addSubtask,
        toggleSubtaskComplete,
        deleteSubtask,
        updateSubtask,
    } = useAppState();

    // Find the task
    const task = tasks.find(t => t.id === id);

    // Editing state
    const [isEditing, setIsEditing] = useState(false);
    const [editTitle, setEditTitle] = useState('');
    const [editDescription, setEditDescription] = useState('');
    const [editPriority, setEditPriority] = useState<Task['priority']>('medium');
    const [editDueDate, setEditDueDate] = useState('');
    const [editTimeStart, setEditTimeStart] = useState('');
    const [editTimeEnd, setEditTimeEnd] = useState('');
    const [editProjectId, setEditProjectId] = useState<string | undefined>();
    const [editRecurrence, setEditRecurrence] = useState<Task['recurrence'] | undefined>();
    const [editReminder, setEditReminder] = useState<number | undefined>();

    // New subtask input
    const [newSubtaskTitle, setNewSubtaskTitle] = useState('');
    const [showAddSubtask, setShowAddSubtask] = useState(false);

    // Editing subtask
    const [editingSubtaskId, setEditingSubtaskId] = useState<string | null>(null);
    const [editSubtaskTitle, setEditSubtaskTitle] = useState('');

    // Initialize edit form when task changes
    useEffect(() => {
        if (task) {
            setEditTitle(task.title);
            setEditDescription(task.description || '');
            setEditPriority(task.priority);
            setEditDueDate(task.dueDate || '');
            setEditTimeStart(task.timeStart || '');
            setEditTimeEnd(task.timeEnd || '');
            setEditProjectId(task.projectId);
            setEditRecurrence(task.recurrence);
            setEditReminder(task.reminderMinutes);
        }
    }, [task]);

    // Handle not found
    if (!task) {
        return (
            <div className="min-h-screen bg-[#F5F8FA] flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-xl font-semibold text-[#1A2832] mb-2">Task not found</h2>
                    <button
                        onClick={() => navigate(-1)}
                        className="text-[#0D5F6B] font-medium"
                    >
                        Go back
                    </button>
                </div>
            </div>
        );
    }

    const handleSave = () => {
        updateTask(task.id, {
            title: editTitle,
            description: editDescription,
            priority: editPriority,
            dueDate: editDueDate || undefined,
            timeStart: editTimeStart || undefined,
            timeEnd: editTimeEnd || undefined,
            projectId: editProjectId,
            recurrence: editRecurrence,
            reminderMinutes: editReminder,
        });
        setIsEditing(false);
    };

    const handleDelete = () => {
        if (confirm('Are you sure you want to delete this task?')) {
            deleteTask(task.id);
            navigate(-1);
        }
    };

    const handleAddSubtask = () => {
        if (newSubtaskTitle.trim()) {
            addSubtask(task.id, newSubtaskTitle.trim());
            setNewSubtaskTitle('');
            setShowAddSubtask(false);
        }
    };

    const handleSaveSubtaskEdit = (subtaskId: string) => {
        if (editSubtaskTitle.trim()) {
            updateSubtask(task.id, subtaskId, { title: editSubtaskTitle.trim() });
        }
        setEditingSubtaskId(null);
        setEditSubtaskTitle('');
    };

    const getPriorityColor = (priority: Task['priority']) => {
        switch (priority) {
            case 'high': return '#EF4444';
            case 'medium': return '#F59E0B';
            case 'normal': return '#10B981';
            default: return '#7B8C9B';
        }
    };

    const currentProject = projects.find(p => p.id === task.projectId);
    const completedSubtasks = task.subtasks.filter(s => s.completed).length;

    return (
        <div className="min-h-screen bg-[#F5F8FA] pb-24">
            {/* Header */}
            <div className="bg-white border-b border-[#DCE4EA] px-4 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate(-1)}
                            className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors"
                        >
                            <ArrowLeft className="w-5 h-5 text-[#1A2832]" />
                        </button>
                        <h1 className="text-lg font-semibold text-[#1A2832]">Task Details</h1>
                    </div>
                    <div className="flex items-center gap-2">
                        {isEditing ? (
                            <>
                                <button
                                    onClick={() => setIsEditing(false)}
                                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                                >
                                    <X className="w-5 h-5 text-[#7B8C9B]" />
                                </button>
                                <button
                                    onClick={handleSave}
                                    className="p-2 hover:bg-[#D9EEF2] rounded-full transition-colors"
                                >
                                    <Save className="w-5 h-5 text-[#0D5F6B]" />
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    onClick={() => setIsEditing(true)}
                                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                                >
                                    <Edit3 className="w-5 h-5 text-[#7B8C9B]" />
                                </button>
                                <button
                                    onClick={handleDelete}
                                    className="p-2 hover:bg-red-50 rounded-full transition-colors"
                                >
                                    <Trash2 className="w-5 h-5 text-red-500" />
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <div className="p-4 space-y-4">
                {/* Main Task Card */}
                <div className="bg-white rounded-2xl p-4 shadow-[0_4px_16px_rgba(13,95,107,0.08)]">
                    {/* Completion Toggle + Title */}
                    <div className="flex items-start gap-3 mb-4">
                        <button
                            onClick={() => toggleTaskComplete(task.id)}
                            className="mt-1 flex-shrink-0"
                        >
                            {task.completed ? (
                                <CheckCircle className="w-6 h-6 text-[#0D5F6B]" />
                            ) : (
                                <Circle className="w-6 h-6 text-[#DCE4EA]" />
                            )}
                        </button>
                        <div className="flex-1">
                            {isEditing ? (
                                <input
                                    type="text"
                                    value={editTitle}
                                    onChange={(e) => setEditTitle(e.target.value)}
                                    className="w-full text-xl font-semibold text-[#1A2832] border-b-2 border-[#0D5F6B] outline-none bg-transparent pb-1"
                                    autoFocus
                                />
                            ) : (
                                <h2 className={`text-xl font-semibold ${task.completed ? 'text-[#7B8C9B] line-through' : 'text-[#1A2832]'}`}>
                                    {task.title}
                                </h2>
                            )}
                        </div>
                    </div>

                    {/* Priority Badge */}
                    <div className="flex items-center gap-2 mb-4">
                        <Flag className="w-4 h-4" style={{ color: getPriorityColor(task.priority) }} />
                        {isEditing ? (
                            <select
                                value={editPriority}
                                onChange={(e) => setEditPriority(e.target.value as Task['priority'])}
                                className="text-sm font-medium px-3 py-1 rounded-full border border-[#DCE4EA] bg-white outline-none"
                            >
                                <option value="high">High Priority</option>
                                <option value="medium">Medium Priority</option>
                                <option value="normal">Normal Priority</option>
                            </select>
                        ) : (
                            <span
                                className="text-sm font-medium px-3 py-1 rounded-full"
                                style={{
                                    backgroundColor: `${getPriorityColor(task.priority)}15`,
                                    color: getPriorityColor(task.priority),
                                }}
                            >
                                {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)} Priority
                            </span>
                        )}
                    </div>

                    {/* Description */}
                    <div className="mb-4">
                        <label className="text-sm font-medium text-[#7B8C9B] mb-1 block">Description</label>
                        {isEditing ? (
                            <textarea
                                value={editDescription}
                                onChange={(e) => setEditDescription(e.target.value)}
                                placeholder="Add a description..."
                                className="w-full p-3 border border-[#DCE4EA] rounded-xl text-[#1A2832] outline-none focus:border-[#0D5F6B] min-h-[100px] resize-none"
                            />
                        ) : (
                            <p className="text-[#4A5C6A]">
                                {task.description || 'No description'}
                            </p>
                        )}
                    </div>

                    {/* Meta Info Grid */}
                    <div className="grid grid-cols-2 gap-3">
                        {/* Due Date */}
                        <div className="flex items-center gap-2 p-3 bg-[#F5F8FA] rounded-xl">
                            <Calendar className="w-4 h-4 text-[#7B8C9B]" />
                            {isEditing ? (
                                <input
                                    type="date"
                                    value={editDueDate}
                                    onChange={(e) => setEditDueDate(e.target.value)}
                                    className="flex-1 bg-transparent outline-none text-sm text-[#1A2832]"
                                />
                            ) : (
                                <span className="text-sm text-[#4A5C6A]">
                                    {task.dueDate || 'No due date'}
                                </span>
                            )}
                        </div>

                        {/* Time */}
                        <div className="flex items-center gap-2 p-3 bg-[#F5F8FA] rounded-xl">
                            <Clock className="w-4 h-4 text-[#7B8C9B]" />
                            {isEditing ? (
                                <div className="flex-1 flex items-center gap-1">
                                    <input
                                        type="time"
                                        value={editTimeStart}
                                        onChange={(e) => setEditTimeStart(e.target.value)}
                                        className="w-20 bg-transparent outline-none text-xs text-[#1A2832]"
                                    />
                                    <span className="text-xs text-[#7B8C9B]">-</span>
                                    <input
                                        type="time"
                                        value={editTimeEnd}
                                        onChange={(e) => setEditTimeEnd(e.target.value)}
                                        className="w-20 bg-transparent outline-none text-xs text-[#1A2832]"
                                    />
                                </div>
                            ) : (
                                <span className="text-sm text-[#4A5C6A]">
                                    {task.timeStart ? `${task.timeStart} - ${task.timeEnd}` : 'No time set'}
                                </span>
                            )}
                        </div>

                        {/* Project */}
                        <div className="col-span-2 flex items-center gap-2 p-3 bg-[#F5F8FA] rounded-xl">
                            <FolderOpen className="w-4 h-4 text-[#7B8C9B]" />
                            {isEditing ? (
                                <select
                                    value={editProjectId || ''}
                                    onChange={(e) => setEditProjectId(e.target.value || undefined)}
                                    className="flex-1 bg-transparent outline-none text-sm text-[#1A2832]"
                                >
                                    <option value="">No Project</option>
                                    {projects.map(p => (
                                        <option key={p.id} value={p.id}>{p.icon} {p.name}</option>
                                    ))}
                                </select>
                            ) : (
                                <span className="text-sm text-[#4A5C6A]">
                                    {currentProject ? `${currentProject.icon} ${currentProject.name}` : 'No project'}
                                </span>
                            )}
                        </div>

                        {/* Recurrence */}
                        <div className="col-span-2 flex items-center gap-2 p-3 bg-[#F5F8FA] rounded-xl">
                            <Repeat className="w-4 h-4 text-[#7B8C9B]" />
                            {isEditing ? (
                                <select
                                    value={editRecurrence?.pattern || ''}
                                    onChange={(e) => {
                                        if (e.target.value === '') {
                                            setEditRecurrence(undefined);
                                        } else {
                                            setEditRecurrence({
                                                pattern: e.target.value as Task['recurrence'] extends { pattern: infer P } ? P : never,
                                                interval: 1,
                                            });
                                        }
                                    }}
                                    className="flex-1 bg-transparent outline-none text-sm text-[#1A2832]"
                                >
                                    <option value="">No Repeat</option>
                                    <option value="daily">Daily</option>
                                    <option value="weekdays">Weekdays</option>
                                    <option value="weekly">Weekly</option>
                                    <option value="monthly">Monthly</option>
                                </select>
                            ) : (
                                <span className="text-sm text-[#4A5C6A]">
                                    {task.recurrence ? (
                                        <>
                                            Repeats {task.recurrence.pattern}
                                            {task.recurrence.interval && task.recurrence.interval > 1 && ` (every ${task.recurrence.interval})`}
                                        </>
                                    ) : 'No repeat'}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Reminder Row */}
                    <div className="flex items-center gap-3 pt-3 border-t border-[#DCE4EA]">
                        <div className="flex items-center gap-2 text-[#7B8C9B]">
                            <Bell className="w-4 h-4" />
                            <span className="text-sm">Reminder</span>
                        </div>
                        <div className="flex-1 flex justify-end">
                            {isEditing ? (
                                <select
                                    value={editReminder ?? ''}
                                    onChange={(e) => setEditReminder(e.target.value ? Number(e.target.value) : undefined)}
                                    className="flex-1 bg-transparent outline-none text-sm text-[#1A2832]"
                                >
                                    <option value="">No reminder</option>
                                    <option value="0">At time</option>
                                    <option value="5">5 minutes before</option>
                                    <option value="15">15 minutes before</option>
                                    <option value="30">30 minutes before</option>
                                    <option value="60">1 hour before</option>
                                    <option value="1440">1 day before</option>
                                </select>
                            ) : (
                                <span className="text-sm text-[#4A5C6A]">
                                    {task.reminderMinutes !== undefined ? (
                                        task.reminderMinutes === 0 ? 'At time' :
                                            task.reminderMinutes === 5 ? '5 min before' :
                                                task.reminderMinutes === 15 ? '15 min before' :
                                                    task.reminderMinutes === 30 ? '30 min before' :
                                                        task.reminderMinutes === 60 ? '1 hour before' :
                                                            task.reminderMinutes === 1440 ? '1 day before' :
                                                                `${task.reminderMinutes} min before`
                                    ) : 'No reminder'}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Subtasks Section */}
            <div className="bg-white rounded-2xl p-4 shadow-[0_4px_16px_rgba(13,95,107,0.08)]">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-[#1A2832]">Subtasks</h3>
                        {task.subtasks.length > 0 && (
                            <span className="text-sm text-[#7B8C9B]">
                                {completedSubtasks}/{task.subtasks.length}
                            </span>
                        )}
                    </div>
                    <button
                        onClick={() => setShowAddSubtask(true)}
                        className="flex items-center gap-1 text-[#0D5F6B] text-sm font-medium"
                    >
                        <Plus className="w-4 h-4" />
                        Add
                    </button>
                </div>

                {/* Add Subtask Input */}
                {showAddSubtask && (
                    <div className="flex items-center gap-2 mb-3 p-2 bg-[#F5F8FA] rounded-xl">
                        <input
                            type="text"
                            value={newSubtaskTitle}
                            onChange={(e) => setNewSubtaskTitle(e.target.value)}
                            placeholder="New subtask..."
                            className="flex-1 bg-transparent outline-none text-sm text-[#1A2832]"
                            autoFocus
                            onKeyDown={(e) => e.key === 'Enter' && handleAddSubtask()}
                        />
                        <button
                            onClick={handleAddSubtask}
                            className="p-1.5 bg-[#0D5F6B] rounded-lg"
                        >
                            <Check className="w-4 h-4 text-white" />
                        </button>
                        <button
                            onClick={() => {
                                setShowAddSubtask(false);
                                setNewSubtaskTitle('');
                            }}
                            className="p-1.5 hover:bg-gray-200 rounded-lg"
                        >
                            <X className="w-4 h-4 text-[#7B8C9B]" />
                        </button>
                    </div>
                )}

                {/* Subtask List */}
                {task.subtasks.length === 0 ? (
                    <p className="text-sm text-[#7B8C9B] text-center py-4">
                        No subtasks yet. Break down this task into smaller steps!
                    </p>
                ) : (
                    <div className="space-y-2">
                        {task.subtasks.map((subtask) => (
                            <div
                                key={subtask.id}
                                className="flex items-center gap-3 p-3 bg-[#F5F8FA] rounded-xl group"
                            >
                                <button
                                    onClick={() => toggleSubtaskComplete(task.id, subtask.id)}
                                    className="flex-shrink-0"
                                >
                                    {subtask.completed ? (
                                        <CheckCircle className="w-5 h-5 text-[#0D5F6B]" />
                                    ) : (
                                        <Circle className="w-5 h-5 text-[#DCE4EA]" />
                                    )}
                                </button>

                                {editingSubtaskId === subtask.id ? (
                                    <div className="flex-1 flex items-center gap-2">
                                        <input
                                            type="text"
                                            value={editSubtaskTitle}
                                            onChange={(e) => setEditSubtaskTitle(e.target.value)}
                                            className="flex-1 bg-white px-2 py-1 rounded border border-[#DCE4EA] outline-none text-sm"
                                            autoFocus
                                            onKeyDown={(e) => e.key === 'Enter' && handleSaveSubtaskEdit(subtask.id)}
                                        />
                                        <button
                                            onClick={() => handleSaveSubtaskEdit(subtask.id)}
                                            className="p-1 bg-[#0D5F6B] rounded"
                                        >
                                            <Check className="w-3 h-3 text-white" />
                                        </button>
                                        <button
                                            onClick={() => setEditingSubtaskId(null)}
                                            className="p-1 hover:bg-gray-200 rounded"
                                        >
                                            <X className="w-3 h-3 text-[#7B8C9B]" />
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <span className={`flex-1 text-sm ${subtask.completed ? 'text-[#7B8C9B] line-through' : 'text-[#1A2832]'}`}>
                                            {subtask.title}
                                        </span>
                                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button
                                                onClick={() => {
                                                    setEditingSubtaskId(subtask.id);
                                                    setEditSubtaskTitle(subtask.title);
                                                }}
                                                className="p-1 hover:bg-white rounded"
                                            >
                                                <Edit3 className="w-3 h-3 text-[#7B8C9B]" />
                                            </button>
                                            <button
                                                onClick={() => deleteSubtask(task.id, subtask.id)}
                                                className="p-1 hover:bg-red-50 rounded"
                                            >
                                                <Trash2 className="w-3 h-3 text-red-400" />
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Quick Actions */}
            <div className="flex gap-3">
                <button
                    onClick={() => toggleTaskComplete(task.id)}
                    className={`flex-1 py-3 rounded-xl font-semibold transition-colors ${task.completed
                        ? 'bg-white text-[#0D5F6B] border-2 border-[#0D5F6B]'
                        : 'bg-[#0D5F6B] text-white'
                        }`}
                >
                    {task.completed ? 'Mark Incomplete' : 'Mark Complete'}
                </button>
                <button
                    onClick={() => navigate(`/app/focus?taskId=${task.id}`)}
                    className="px-6 py-3 bg-white text-[#0D5F6B] border border-[#DCE4EA] rounded-xl font-semibold"
                >
                    Focus
                </button>
            </div>
        </div>
    );
}
